/**
 * Client side of the annihilator auction (design D7, port addition): the original "king of the hill" auction on the
 * own backend, paid in diamonds instead of VK votes. The backend's auction (auction.get / the auction.buy result,
 * src/shared/protocol/model.ts AuctionState) is turned into the memory the original screens read: FWP.auc_info
 * (price, holder, time of the last purchase, server time), FWP.auc_names[0] (the holder's name) and the player's
 * Annihilators (auc_weapons = the held one, win_auc_weapons = the permanent copies; the arsenal shows their sum).
 * A battle takes its Annihilators from the server (battle.start), not from this memory.
 *
 * As everywhere in the game, the Russian text is the source and the translation key (tools/i18n/extra.json).
 */
import { netErrorCode } from './net-policy';

/** The auction cannot be opened (the server has it switched off, or no backend offers it). */
export const MSG_AUCTION_UNAVAILABLE = 'Аукцион сейчас недоступен.';

/** What the original screens read, built from the backend's auction. */
export interface AuctionMemory {
  /** FWP.auc_info: price, holder (numeric uid and backend id), last purchase and server time in unix seconds. */
  info: { user_id: number; holder: string; price: number; last_buy: number; cur_time: number; ends: number; round: number; winners: number };
  /** FWP.auc_names: the holder's name in the original's VK-profile shape. */
  names: any;
  /** The player's Annihilator of the current round (auc_weapons). */
  held: number;
  /** The player's permanent copies of won rounds (win_auc_weapons). */
  permanent: number;
}

const seconds = (ms: unknown): number => (typeof ms === 'number' && ms > 0 ? Math.floor(ms / 1000) : 0);

/**
 * The auction `state` (AuctionState) and the player's holdings `mine` ({ held, permanent }) at server time `nowSec`
 * (unix seconds) as the original's memory; `uidOf` maps a backend player id to the game's numeric uid.
 */
export function auctionMemory(state: any, mine: any, nowSec: number, uidOf: (playerId: string) => number): AuctionMemory {
  const holder = state && state.holder ? String(state.holder.playerId) : '';
  return {
    info: {
      user_id: holder ? uidOf(holder) : 0,
      holder,
      price: Number(state && state.price) || 0,
      last_buy: seconds(state && state.since),
      cur_time: nowSec,
      ends: seconds(state && state.endsAt),
      round: Number(state && state.round) || 1,
      winners: Number(state && state.winnerCount) || 0,
    },
    names: { 0: { uid: holder ? uidOf(holder) : 0, first_name: holder ? String(state.holder.displayName) : '', last_name: '' } },
    held: Number(mine && mine.held) || 0,
    permanent: Number(mine && mine.permanent) || 0,
  };
}

/**
 * auction.get for the auction screen: `{ info }`, or `{ refused }` when the server does not offer the auction
 * (FORBIDDEN 'feature_disabled', or a backend without it); other failures are the bridge's (retry, ban, ...).
 */
export async function loadAuction(services: any): Promise<{ info?: any; refused?: any }> {
  if (!services || typeof services.getAuction !== 'function') return { refused: { code: 'NOT_FOUND' } };
  try {
    return { info: await services.getAuction() };
  } catch (error) {
    const code = netErrorCode(error);
    if (code === 'FORBIDDEN' || code === 'NOT_FOUND' || code === 'BAD_REQUEST') return { refused: error };
    throw error;
  }
}
